import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandResult } from 'src/core/stores/types';
import { NewUserItem, UserItem, UserItemStore } from 'src/stores/users-store';
import { container } from 'src/inversify.config';
import { Col, Input, Row, Spin, Switch, Form } from 'antd';
import { nameof } from 'src/core/utils/object';
import OrganizationsSelect from 'src/common/components/business/organization-select';
import LanguageSelect from 'src/common/components/business/language-select';
import { useIdentity } from 'src/core/services/authentication';
import NewItemModal from 'src/core/ui/newitemmodal';

interface NewUserDialogFormData {
  email: string;
  displayName: string;
  password: string;
  organization: { value: string; label: string };
  sendInvitation: boolean;
  defaultLanguage?: string;
}

const NewUserDialog: FC<{ onClose: (response?: CommandResult<UserItem>) => void }> = (props) => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const { onClose } = props;
  const [form] = Form.useForm();
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [sendInvitationCheck, setSendInvitationCheck] = useState(false);

  const newUserStore = container.get(UserItemStore);
  const newUserState = newUserStore.state;

  const onNewItem = async () => {
    let item = (await form.validateFields()) as NewUserDialogFormData;
    item.sendInvitation = sendInvitationCheck;
    const data: NewUserItem = {
      displayName: item.displayName,
      email: item.email,
      password: item.password,
      organizationId: item.organization?.value,
      sendInvitation: item.sendInvitation,
      defaultLanguage: item.defaultLanguage,
    };

    if (item.password && item.password !== (item as any).confirmPassword) {
      newUserState.errorMessage.set(t('PasswordMustMatch'));
      return;
    }
    let response = await newUserStore.create(data);
    if (response && response?.isSuccess) {
      form.resetFields();
      onClose(response);
    }
  };

  function onChange(checked: boolean) {
    setSendInvitationCheck(checked);
  }

  return (
    <NewItemModal
      className="figma-modal"
      title={t('AddNewUser')}
      onNewItem={onNewItem}
      onClose={onClose}
      onOkButtonIsDisabled={newUserState.isBusy.get()}
      onCancelButtonIsDisabled={newUserState.isBusy.get()}
      okText={t('AddUser')}
      errorMessage={newUserState.errorMessage}
      closable={!newUserState.isBusy.get()}
      maskClosable={!newUserState.isBusy.get()}
      body={
        <>
          <Spin spinning={newUserState.isBusy.get()}>
            <Form form={form} layout="vertical" autoComplete="off">
              <Form.Item name={nameof<NewUserItem>('email')} rules={[{ required: true, type: 'email', message: t('PleaseEnterAValidEmail') }]}>
                <Input allowClear autoComplete="off" placeholder={t('Email')} />
              </Form.Item>
              <Form.Item name={nameof<NewUserItem>('displayName')} rules={[{ required: true, message: t('PleaseEnterName') }]}>
                <Input allowClear autoComplete="off" placeholder={t('DisplayName')} />
              </Form.Item>
              <Form.Item name="organization" rules={[{ required: true, message: t('PleaseSelectAnOrganization') }]}>
                <OrganizationsSelect />
              </Form.Item>
              <Form.Item name="defaultLanguage" initialValue={identity.profile['default_lang']} rules={[{ required: false }]}>
                <LanguageSelect />
              </Form.Item>
              <Form.Item noStyle>
                <Row>
                  <Col span={12}>
                    <Form.Item>
                      <Row>
                        <Switch
                          checked={showPasswordFields}
                          onChange={(checked) => {
                            setShowPasswordFields(!!checked);
                            form.setFieldsValue({ ...form.getFieldsValue(), password: undefined, confirmPassword: undefined });
                          }}
                        />
                        <h5 style={{ marginLeft: 10 }}>{t('SetPassword')}</h5>
                      </Row>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="sendInvitation" valuePropName="checked" rules={[{ required: false }]}>
                      <Row>
                        <Switch onChange={onChange} />
                        <h5 style={{ marginLeft: 10 }}>{t('SendInvitation')}</h5>
                      </Row>
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
              {showPasswordFields && (
                <>
                  <Form.Item name={nameof<NewUserItem>('password')} label={t('Password')} rules={[{ required: false }]}>
                    <Input.Password allowClear autoComplete="off" />
                  </Form.Item>
                  <Form.Item name={'confirmPassword'} label={t('ConfirmPassword')} rules={[{ required: false }]}>
                    <Input.Password allowClear autoComplete="off" />
                  </Form.Item>
                  <small>{t('BalnkPasswordWarning')}</small>
                </>
              )}
            </Form>
          </Spin>
        </>
      }
    />
  );
};

export default NewUserDialog;
